import { useId } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { METADATA_TYPE, type MetadataType } from "@/constants/metadata-types"
import {
  Button,
  GroupLabel,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { ListFilter, Minus, Plus } from "lucide-react"

import { EntitySelectionCard } from "@/components/entity-picker/EntitySelectionCard"
import { KeyValueRowsEditor } from "@/components/forms/KeyValueRowsEditor"
import type {
  ConversationMetricRule,
  ConversationMetricRuleCondition,
} from "@/types/api/ConversationMetricRule"

import { CONVERSATION_METRIC_RULE_MATCH_TYPES } from "../utils/conversationMetricRulePresentation"

interface MetricDisplay {
  metric_display_name?: string
}

interface ConversationMetricRuleFormProps {
  metricsByName?: Record<string, MetricDisplay>
  selectedMetrics: string[]
  metadataConditions: ConversationMetricRuleCondition[]
  isMetricsLoading: boolean
  showConditionsWhenNoMetrics?: boolean
  onRemoveMetric: (metricName: string) => void
  onBrowseMetrics: () => void
  onAddMetadataCondition: () => void
  onRemoveMetadataCondition: (conditionIndex: number) => void
  onSetMetadataConditionKey: (
    conditionIndex: number,
    metadataKey: string,
  ) => void
  onSetMetadataConditionType: (
    conditionIndex: number,
    metadataType: MetadataType,
  ) => void
  onSetMetadataConditionMatchType: (
    conditionIndex: number,
    matchType: ConversationMetricRule["matchType"],
  ) => void
  onSetMetadataConditionValue: (
    conditionIndex: number,
    metadataValue: string | boolean | number | string[],
  ) => void
  onSetBooleanConditionValue: (conditionIndex: number, value: string) => void
  onAddArrayConditionValue: (conditionIndex: number) => void
  onUpdateArrayConditionValue: (
    conditionIndex: number,
    valueIndex: number,
    value: string,
  ) => void
  onRemoveArrayConditionValue: (
    conditionIndex: number,
    valueIndex: number,
  ) => void
}

interface ComparisonOption {
  description: string
  label: string
  matchType: ConversationMetricRule["matchType"]
  metadataType: MetadataType
  value: string
}

const comparisonValue = (
  metadataType: MetadataType,
  matchType: ConversationMetricRule["matchType"],
) =>
  metadataType === METADATA_TYPE.BOOLEAN
    ? `${metadataType}:IS`
    : `${metadataType}:${matchType}`

const comparisonGroups: Array<{
  label: string
  options: ComparisonOption[]
}> = [
  {
    label: "Text",
    options: CONVERSATION_METRIC_RULE_MATCH_TYPES.STRING.map((option) => ({
      ...option,
      matchType: option.value,
      metadataType: METADATA_TYPE.STRING,
      value: comparisonValue(METADATA_TYPE.STRING, option.value),
    })),
  },
  {
    label: "Boolean",
    options: [
      {
        description: "Matches whether the metadata value is true or false.",
        label: "Is",
        matchType: "IS_TRUE",
        metadataType: METADATA_TYPE.BOOLEAN,
        value: comparisonValue(METADATA_TYPE.BOOLEAN, "IS_TRUE"),
      },
    ],
  },
  {
    label: "List",
    options: CONVERSATION_METRIC_RULE_MATCH_TYPES.STRING_ARRAY.map(
      (option) => ({
        ...option,
        matchType: option.value,
        metadataType: METADATA_TYPE.STRING_ARRAY,
        value: comparisonValue(METADATA_TYPE.STRING_ARRAY, option.value),
      }),
    ),
  },
]

const comparisonOptions = comparisonGroups.flatMap((group) => group.options)

interface MetadataComparisonSelectProps {
  condition: ConversationMetricRuleCondition
  conditionIndex: number
  onSetMetadataConditionType: ConversationMetricRuleFormProps["onSetMetadataConditionType"]
  onSetMetadataConditionMatchType: ConversationMetricRuleFormProps["onSetMetadataConditionMatchType"]
}

function MetadataComparisonSelect({
  condition,
  conditionIndex,
  onSetMetadataConditionType,
  onSetMetadataConditionMatchType,
}: MetadataComparisonSelectProps) {
  const value = comparisonValue(condition.metadataType, condition.matchType)
  const selectedOption = comparisonOptions.find(
    (option) => option.value === value,
  )

  return (
    <Select
      value={value}
      onValueChange={(nextValue) => {
        const nextOption = comparisonOptions.find(
          (option) => option.value === nextValue,
        )
        if (!nextOption) {
          return
        }

        const isMetadataTypeChange =
          nextOption.metadataType !== condition.metadataType
        if (isMetadataTypeChange) {
          onSetMetadataConditionType(conditionIndex, nextOption.metadataType)
        }
        if (
          nextOption.metadataType !== METADATA_TYPE.BOOLEAN &&
          (isMetadataTypeChange || nextOption.matchType !== condition.matchType)
        ) {
          onSetMetadataConditionMatchType(conditionIndex, nextOption.matchType)
        }
      }}
    >
      <SelectTrigger
        aria-label={`Comparison ${conditionIndex + 1}`}
        className="w-36 shrink-0"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <ListFilter className="size-3.5 shrink-0 text-coval-text-muted" />
          <span className="truncate">{selectedOption?.label ?? "Compare"}</span>
        </span>
      </SelectTrigger>
      <SelectContent className="min-w-64">
        {comparisonGroups.map((group) => (
          <SelectGroup key={group.label}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  )
}

interface MetadataConditionValueProps {
  condition: ConversationMetricRuleCondition
  conditionIndex: number
  onSetMetadataConditionValue: ConversationMetricRuleFormProps["onSetMetadataConditionValue"]
  onSetBooleanConditionValue: ConversationMetricRuleFormProps["onSetBooleanConditionValue"]
  onAddArrayConditionValue: ConversationMetricRuleFormProps["onAddArrayConditionValue"]
  onUpdateArrayConditionValue: ConversationMetricRuleFormProps["onUpdateArrayConditionValue"]
  onRemoveArrayConditionValue: ConversationMetricRuleFormProps["onRemoveArrayConditionValue"]
}

function MetadataConditionValue({
  condition,
  conditionIndex,
  onSetMetadataConditionValue,
  onSetBooleanConditionValue,
  onAddArrayConditionValue,
  onUpdateArrayConditionValue,
  onRemoveArrayConditionValue,
}: MetadataConditionValueProps) {
  const ariaLabel = `Metadata value ${conditionIndex + 1}`

  if (condition.metadataType === METADATA_TYPE.BOOLEAN) {
    return (
      <Select
        value={(condition.metadataValue as boolean).toString()}
        onValueChange={(value) =>
          onSetBooleanConditionValue(conditionIndex, value)
        }
      >
        <SelectTrigger aria-label={ariaLabel} className="min-w-0 flex-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  if (
    condition.metadataType === METADATA_TYPE.STRING_ARRAY &&
    condition.matchType === "LENGTH_AT_LEAST"
  ) {
    return (
      <Input
        type="number"
        min={0}
        step={1}
        aria-label={`Minimum entries ${conditionIndex + 1}`}
        placeholder="Minimum entries"
        value={
          typeof condition.metadataValue === "number"
            ? condition.metadataValue
            : ""
        }
        onChange={(event) => {
          const parsed = Number.parseInt(event.target.value, 10)
          onSetMetadataConditionValue(
            conditionIndex,
            Number.isNaN(parsed) ? "" : Math.max(0, parsed),
          )
        }}
        className="min-w-0 flex-1"
      />
    )
  }

  if (condition.metadataType === METADATA_TYPE.STRING_ARRAY) {
    const values = Array.isArray(condition.metadataValue)
      ? condition.metadataValue
      : []

    return (
      <div className="flex min-w-0 flex-1 flex-wrap gap-2">
        {values.map((value, valueIndex) => (
          <div
            key={`${conditionIndex}-${valueIndex}`}
            className="flex min-w-40 flex-1 items-center gap-1"
          >
            <Input
              aria-label={`${ariaLabel}.${valueIndex + 1}`}
              placeholder="Metadata value"
              value={value}
              onChange={(event) =>
                onUpdateArrayConditionValue(
                  conditionIndex,
                  valueIndex,
                  event.target.value,
                )
              }
              className="min-w-0 flex-1"
            />
            <Button
              type="button"
              icon
              variant="ghost"
              tooltip={`Remove metadata value ${conditionIndex + 1}.${valueIndex + 1}`}
              onClick={() =>
                onRemoveArrayConditionValue(conditionIndex, valueIndex)
              }
            >
              <Minus className="size-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          icon
          variant="ghost"
          tooltip={`Add metadata value ${conditionIndex + 1}`}
          onClick={() => onAddArrayConditionValue(conditionIndex)}
        >
          <Plus className="size-4" />
        </Button>
      </div>
    )
  }

  return (
    <Input
      aria-label={ariaLabel}
      placeholder="Metadata value"
      value={
        typeof condition.metadataValue === "string"
          ? condition.metadataValue
          : ""
      }
      onChange={(event) =>
        onSetMetadataConditionValue(conditionIndex, event.target.value)
      }
      className="min-w-0 flex-1"
    />
  )
}

export function ConversationMetricRuleForm({
  metricsByName,
  selectedMetrics,
  metadataConditions,
  isMetricsLoading,
  showConditionsWhenNoMetrics = false,
  onRemoveMetric,
  onBrowseMetrics,
  onAddMetadataCondition,
  onRemoveMetadataCondition,
  onSetMetadataConditionKey,
  onSetMetadataConditionType,
  onSetMetadataConditionMatchType,
  onSetMetadataConditionValue,
  onSetBooleanConditionValue,
  onAddArrayConditionValue,
  onUpdateArrayConditionValue,
  onRemoveArrayConditionValue,
}: ConversationMetricRuleFormProps) {
  const conditionsLabelId = useId()
  const metricChips = selectedMetrics.map((metricName) => ({
    id: metricName,
    label: metricsByName?.[metricName]?.metric_display_name || metricName,
  }))
  const conditionRows = metadataConditions.map((condition, index) => ({
    id: index,
    key: condition.metadataKey,
    value: "",
  }))

  return (
    <div className="space-y-5">
      <EntitySelectionCard
        icon={ENTITY_ICONS.metrics}
        title="Metrics"
        description="Choose the metrics to add when every condition matches."
        chips={metricChips}
        onRemove={onRemoveMetric}
        onBrowse={onBrowseMetrics}
        browseLabel="Add Metric"
        addLabel="Add Metric"
        disabled={isMetricsLoading}
      />

      {selectedMetrics.length > 0 || showConditionsWhenNoMetrics ? (
        <div
          role="group"
          aria-labelledby={conditionsLabelId}
          className="space-y-2"
        >
          <GroupLabel id={conditionsLabelId}>Conditions</GroupLabel>
          <KeyValueRowsEditor
            rows={conditionRows}
            onChange={(conditionIndex, patch) => {
              if (patch.key !== undefined) {
                onSetMetadataConditionKey(conditionIndex, patch.key)
              }
            }}
            onRemove={onRemoveMetadataCondition}
            onAdd={onAddMetadataCondition}
            keyLabel="Metadata key"
            valueLabel="Metadata value"
            addLabel="Add condition"
            canRemove={() => metadataConditions.length > 1}
            getKeyLabel={(row) => `Metadata key ${row.id + 1}`}
            getRemoveLabel={(row) => `Remove condition ${row.id + 1}`}
            renderMiddle={(row) => {
              const condition = metadataConditions[row.id]
              return condition ? (
                <MetadataComparisonSelect
                  condition={condition}
                  conditionIndex={row.id}
                  onSetMetadataConditionType={onSetMetadataConditionType}
                  onSetMetadataConditionMatchType={
                    onSetMetadataConditionMatchType
                  }
                />
              ) : null
            }}
            renderValue={(row) => {
              const condition = metadataConditions[row.id]
              return condition ? (
                <MetadataConditionValue
                  condition={condition}
                  conditionIndex={row.id}
                  onSetMetadataConditionValue={onSetMetadataConditionValue}
                  onSetBooleanConditionValue={onSetBooleanConditionValue}
                  onAddArrayConditionValue={onAddArrayConditionValue}
                  onUpdateArrayConditionValue={onUpdateArrayConditionValue}
                  onRemoveArrayConditionValue={onRemoveArrayConditionValue}
                />
              ) : null
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
